import { t } from '../i18n';

/**
 * SDK Error Hierarchy
 *
 * Provides structured, typed errors for better error handling,
 * debugging, and user experience.
 */

// ============================================================
// Error Codes
// ============================================================

/**
 * Error codes for SDK errors
 */
export enum SDKErrorCode {
  // Camera errors (1xx)
  CAMERA_ACCESS_DENIED = 'CAMERA_ACCESS_DENIED',
  CAMERA_NOT_FOUND = 'CAMERA_NOT_FOUND',
  CAMERA_IN_USE = 'CAMERA_IN_USE',
  CAMERA_STREAM_ERROR = 'CAMERA_STREAM_ERROR',

  // Network errors (2xx)
  NETWORK_ERROR = 'NETWORK_ERROR',
  NETWORK_TIMEOUT = 'NETWORK_TIMEOUT',
  NETWORK_OFFLINE = 'NETWORK_OFFLINE',
  API_ERROR = 'API_ERROR',

  // Validation errors (3xx)
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_API_KEY = 'INVALID_API_KEY',
  INVALID_CONFIG = 'INVALID_CONFIG',
  INVALID_URL = 'INVALID_URL',

  // Security errors (4xx)
  SECURITY_ERROR = 'SECURITY_ERROR',
  OPEN_REDIRECT_BLOCKED = 'OPEN_REDIRECT_BLOCKED',
  UNTRUSTED_ORIGIN = 'UNTRUSTED_ORIGIN',

  // Processing errors (5xx)
  FACE_NOT_DETECTED = 'FACE_NOT_DETECTED',
  LIVENESS_FAILED = 'LIVENESS_FAILED',
  AGE_ESTIMATION_FAILED = 'AGE_ESTIMATION_FAILED',
  DOCUMENT_CAPTURE_FAILED = 'DOCUMENT_CAPTURE_FAILED',
  OCR_PROCESSING_FAILED = 'OCR_PROCESSING_FAILED',

  // System errors (9xx)
  INITIALIZATION_ERROR = 'INITIALIZATION_ERROR',
  MODEL_LOAD_ERROR = 'MODEL_LOAD_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

// ============================================================
// Base SDK Error
// ============================================================

/**
 * Base error class for all SDK errors
 */
export abstract class SDKError extends Error {
  /** Error code for programmatic handling */
  abstract readonly code: SDKErrorCode;

  /** Timestamp when the error occurred */
  readonly timestamp: number;

  /** Additional context about the error */
  readonly context?: string;

  /** User-friendly message (suitable for display) */
  readonly userMessage?: string;

  /** Whether this error is recoverable */
  readonly recoverable: boolean;

  /** Original error that caused this one (ES2022 compatible) */
  readonly cause?: Error;

  constructor(
    message: string,
    options?: {
      context?: string;
      userMessage?: string;
      recoverable?: boolean;
      cause?: Error;
    }
  ) {
    super(message);
    this.name = this.constructor.name;
    this.timestamp = Date.now();
    this.context = options?.context;
    this.userMessage = options?.userMessage;
    this.recoverable = options?.recoverable ?? false;
    this.cause = options?.cause;

    // Maintain proper stack trace in V8 environments
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Returns a JSON-serializable representation of the error
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      userMessage: this.userMessage,
      context: this.context,
      timestamp: this.timestamp,
      recoverable: this.recoverable,
    };
  }
}

// ============================================================
// Camera Errors
// ============================================================

/**
 * Error thrown when camera access fails
 */
export class CameraError extends SDKError {
  readonly code: SDKErrorCode;

  constructor(
    code:
      | SDKErrorCode.CAMERA_ACCESS_DENIED
      | SDKErrorCode.CAMERA_NOT_FOUND
      | SDKErrorCode.CAMERA_IN_USE
      | SDKErrorCode.CAMERA_STREAM_ERROR,
    message: string,
    options?: { context?: string; userMessage?: string; recoverable?: boolean; cause?: Error }
  ) {
    super(message, { recoverable: true, ...options });
    this.code = code;
  }

  static accessDenied(cause?: Error): CameraError {
    return new CameraError(SDKErrorCode.CAMERA_ACCESS_DENIED, 'Camera access was denied', {
      userMessage: t(
        'errors.cameraAccessDeniedDetailed',
        'Please allow camera access to continue with verification.'
      ),
      recoverable: true,
      cause,
    });
  }

  static notFound(): CameraError {
    return new CameraError(SDKErrorCode.CAMERA_NOT_FOUND, 'No camera found on this device', {
      userMessage: t(
        'errors.cameraNotFoundDetailed',
        'No camera was detected. Please connect a camera and try again.'
      ),
      recoverable: false,
    });
  }

  static inUse(): CameraError {
    return new CameraError(SDKErrorCode.CAMERA_IN_USE, 'Camera is in use by another application', {
      userMessage: t(
        'errors.cameraInUseDetailed',
        'The camera is being used by another app. Please close it and try again.'
      ),
      recoverable: true,
    });
  }
}

// ============================================================
// Network Errors
// ============================================================

/**
 * Error thrown when network operations fail
 */
export class NetworkError extends SDKError {
  readonly code: SDKErrorCode;

  /** HTTP status code if applicable */
  readonly statusCode?: number;

  constructor(
    code:
      | SDKErrorCode.NETWORK_ERROR
      | SDKErrorCode.NETWORK_TIMEOUT
      | SDKErrorCode.NETWORK_OFFLINE
      | SDKErrorCode.API_ERROR,
    message: string,
    options?: {
      statusCode?: number;
      context?: string;
      userMessage?: string;
      recoverable?: boolean;
      cause?: Error;
    }
  ) {
    super(message, { recoverable: true, ...options });
    this.code = code;
    this.statusCode = options?.statusCode;
  }

  static timeout(endpoint?: string): NetworkError {
    return new NetworkError(
      SDKErrorCode.NETWORK_TIMEOUT,
      `Request timed out${endpoint ? ` for ${endpoint}` : ''}`,
      {
        context: endpoint,
        userMessage: t(
          'errors.networkTimeout',
          'The request took too long. Please check your connection and try again.'
        ),
        recoverable: true,
      }
    );
  }

  static offline(): NetworkError {
    return new NetworkError(SDKErrorCode.NETWORK_OFFLINE, 'Device is offline', {
      userMessage: t(
        'errors.networkOffline',
        'You appear to be offline. Please check your internet connection.'
      ),
      recoverable: true,
    });
  }

  static apiError(statusCode: number, message: string, endpoint?: string): NetworkError {
    return new NetworkError(SDKErrorCode.API_ERROR, `API error ${statusCode}: ${message}`, {
      statusCode,
      context: endpoint,
      userMessage: t('errors.apiGenericError', 'Something went wrong. Please try again.'),
      recoverable: statusCode >= 500, // Server errors are potentially recoverable
    });
  }
}

// ============================================================
// Validation Errors
// ============================================================

/**
 * Error thrown when validation fails
 */
export class ValidationError extends SDKError {
  readonly code: SDKErrorCode;

  /** Field that failed validation */
  readonly field?: string;

  constructor(
    code:
      | SDKErrorCode.VALIDATION_ERROR
      | SDKErrorCode.INVALID_API_KEY
      | SDKErrorCode.INVALID_CONFIG
      | SDKErrorCode.INVALID_URL,
    message: string,
    options?: { field?: string; context?: string; userMessage?: string; cause?: Error }
  ) {
    super(message, { recoverable: false, ...options });
    this.code = code;
    this.field = options?.field;
  }

  static invalidApiKey(reason?: string): ValidationError {
    return new ValidationError(
      SDKErrorCode.INVALID_API_KEY,
      `Invalid API key${reason ? `: ${reason}` : ''}`,
      {
        field: 'api_key',
        userMessage: t('errors.invalidApiKey', 'Invalid API key. Please check your configuration.'),
      }
    );
  }

  static invalidConfig(field: string, reason: string): ValidationError {
    return new ValidationError(SDKErrorCode.INVALID_CONFIG, `Invalid configuration: ${reason}`, {
      field,
      userMessage: t('errors.configError', 'Configuration error. Please contact support.'),
    });
  }

  static missingRequired(field: string): ValidationError {
    return new ValidationError(SDKErrorCode.VALIDATION_ERROR, `Missing required field: ${field}`, {
      field,
    });
  }
}

// ============================================================
// Security Errors
// ============================================================

/**
 * Error thrown for security-related issues
 */
export class SecurityError extends SDKError {
  readonly code: SDKErrorCode;

  /** URL that was blocked (if applicable) */
  readonly blockedUrl?: string;

  constructor(
    code:
      | SDKErrorCode.SECURITY_ERROR
      | SDKErrorCode.OPEN_REDIRECT_BLOCKED
      | SDKErrorCode.UNTRUSTED_ORIGIN,
    message: string,
    options?: { blockedUrl?: string; context?: string; cause?: Error }
  ) {
    super(message, { recoverable: false, ...options });
    this.code = code;
    this.blockedUrl = options?.blockedUrl;
  }

  static openRedirectBlocked(url: string, reason: string): SecurityError {
    return new SecurityError(
      SDKErrorCode.OPEN_REDIRECT_BLOCKED,
      `Blocked redirect to untrusted URL: ${reason}`,
      { blockedUrl: url, context: reason }
    );
  }

  static untrustedOrigin(origin: string): SecurityError {
    return new SecurityError(SDKErrorCode.UNTRUSTED_ORIGIN, `Untrusted origin: ${origin}`, {
      blockedUrl: origin,
    });
  }
}

// ============================================================
// Processing Errors
// ============================================================

/**
 * Error thrown when verification processing fails
 */
export class ProcessingError extends SDKError {
  readonly code: SDKErrorCode;

  /** Step that failed */
  readonly step?: string;

  constructor(
    code:
      | SDKErrorCode.FACE_NOT_DETECTED
      | SDKErrorCode.LIVENESS_FAILED
      | SDKErrorCode.AGE_ESTIMATION_FAILED
      | SDKErrorCode.DOCUMENT_CAPTURE_FAILED
      | SDKErrorCode.OCR_PROCESSING_FAILED,
    message: string,
    options?: {
      step?: string;
      context?: string;
      userMessage?: string;
      recoverable?: boolean;
      cause?: Error;
    }
  ) {
    super(message, { recoverable: true, ...options });
    this.code = code;
    this.step = options?.step;
  }

  static faceNotDetected(): ProcessingError {
    return new ProcessingError(SDKErrorCode.FACE_NOT_DETECTED, 'No face detected in frame', {
      step: 'face_detection',
      userMessage: t('errors.faceNotDetected', 'Please position your face in the frame.'),
      recoverable: true,
    });
  }

  static livenessFailed(reason?: string): ProcessingError {
    return new ProcessingError(
      SDKErrorCode.LIVENESS_FAILED,
      `Liveness check failed${reason ? `: ${reason}` : ''}`,
      {
        step: 'liveness_check',
        userMessage: t('errors.livenessFailed', 'Liveness check failed. Please try again.'),
        recoverable: true,
      }
    );
  }

  static documentCaptureFailed(reason?: string): ProcessingError {
    return new ProcessingError(
      SDKErrorCode.DOCUMENT_CAPTURE_FAILED,
      `Document capture failed${reason ? `: ${reason}` : ''}`,
      {
        step: 'document_capture',
        userMessage: t(
          'errors.documentCaptureFailed',
          'Could not capture document. Please ensure good lighting and try again.'
        ),
        recoverable: true,
      }
    );
  }
}

// ============================================================
// System Errors
// ============================================================

/**
 * Error thrown for system-level failures
 */
export class SystemError extends SDKError {
  readonly code: SDKErrorCode;

  constructor(
    code:
      | SDKErrorCode.INITIALIZATION_ERROR
      | SDKErrorCode.MODEL_LOAD_ERROR
      | SDKErrorCode.UNKNOWN_ERROR,
    message: string,
    options?: { context?: string; userMessage?: string; cause?: Error }
  ) {
    super(message, { recoverable: false, ...options });
    this.code = code;
  }

  static initializationFailed(component: string, cause?: Error): SystemError {
    return new SystemError(SDKErrorCode.INITIALIZATION_ERROR, `Failed to initialize ${component}`, {
      context: component,
      userMessage: t(
        'errors.initializationFailed',
        'Failed to load verification system. Please refresh and try again.'
      ),
      cause,
    });
  }

  static modelLoadFailed(modelName: string, cause?: Error): SystemError {
    return new SystemError(SDKErrorCode.MODEL_LOAD_ERROR, `Failed to load model: ${modelName}`, {
      context: modelName,
      userMessage: t(
        'errors.modelLoadFailed',
        'Failed to load required components. Please refresh and try again.'
      ),
      cause,
    });
  }

  static unknown(cause?: Error): SystemError {
    return new SystemError(
      SDKErrorCode.UNKNOWN_ERROR,
      cause?.message || 'An unknown error occurred',
      {
        userMessage: t('errors.unexpectedError', 'An unexpected error occurred. Please try again.'),
        cause,
      }
    );
  }
}

// ============================================================
// Error Utilities
// ============================================================

/**
 * Type guard to check if an error is an SDK error
 */
export function isSDKError(error: unknown): error is SDKError {
  return error instanceof SDKError;
}

/**
 * Wraps an unknown error into an SDK error
 */
export function wrapError(error: unknown, context?: string): SDKError {
  if (isSDKError(error)) {
    return error;
  }

  if (error instanceof Error) {
    return SystemError.unknown(error);
  }

  return new SystemError(SDKErrorCode.UNKNOWN_ERROR, String(error), { context });
}

/**
 * Gets user-friendly message from any error
 */
export function getUserMessage(error: unknown): string {
  if (isSDKError(error)) {
    return error.userMessage || error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return t('errors.unexpectedError', 'An unexpected error occurred');
}
